import type { ChatMessage, ChatRoom, AiConversation, AiMessage } from '@vtak/shared';
import { CLIENT_CONFIG } from '@/config';
import { authHeaders } from '@/services/api-client';

/** Chat API client */
export const chatClient = {
  /** Get all chat rooms */
  async getRooms(): Promise<ChatRoom[]> {
    const res = await fetch(`${CLIENT_CONFIG.apiBase}/api/chat/rooms`, authHeaders());
    if (!res.ok) throw new Error(`Failed to fetch rooms (${res.status})`);
    return res.json() as Promise<ChatRoom[]>;
  },

  /** Create a new chat room */
  async createRoom(name: string): Promise<ChatRoom> {
    const res = await fetch(`${CLIENT_CONFIG.apiBase}/api/chat/rooms`, authHeaders({
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name }),
    }));
    if (!res.ok) throw new Error(`Failed to create room (${res.status})`);
    return res.json() as Promise<ChatRoom>;
  },

  /** Get messages for a room */
  async getMessages(roomId: string, limit: number = 100): Promise<ChatMessage[]> {
    const res = await fetch(
      `${CLIENT_CONFIG.apiBase}/api/chat/rooms/${encodeURIComponent(roomId)}/messages?limit=${limit}`,
      authHeaders(),
    );
    if (!res.ok) throw new Error(`Failed to fetch messages (${res.status})`);
    return res.json() as Promise<ChatMessage[]>;
  },

  /** Send a message to a room */
  async sendMessage(
    roomId: string,
    senderUid: string,
    senderCallsign: string,
    content: string,
  ): Promise<ChatMessage | null> {
    const res = await fetch(
      `${CLIENT_CONFIG.apiBase}/api/chat/rooms/${encodeURIComponent(roomId)}/messages`,
      authHeaders({
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ senderUid, senderCallsign, content }),
      }),
    );
    if (!res.ok) throw new Error(`Failed to send message (${res.status})`);
    return res.json() as Promise<ChatMessage>;
  },

  /**
   * Stream an AI response for a room via SSE.
   * Yields text chunks as they arrive. Throws on HTTP or stream errors
   * so the caller can display feedback and restore the user's message.
   */
  async *streamAiMessage(
    roomId: string,
    senderUid: string,
    senderCallsign: string,
    content: string,
  ): AsyncGenerator<string> {
    const res = await fetch(
      `${CLIENT_CONFIG.apiBase}/api/ai/chat/${encodeURIComponent(roomId)}`,
      authHeaders({
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ senderUid, senderCallsign, content }),
      }),
    );

    if (!res.ok) {
      if (res.status === 404) {
        throw new Error('AI assistant is not configured — add OPENAI_API_KEY to the server');
      }
      const body = await res.json().catch(() => ({ error: 'Unknown error' })) as { error?: string };
      throw new Error(body.error ?? `AI request failed (${res.status})`);
    }

    if (!res.body) throw new Error('No response stream available');

    yield* readSseStream(res.body);
  },

  /** Get all AI conversations for the current user */
  async getAiConversations(): Promise<AiConversation[]> {
    const res = await fetch(`${CLIENT_CONFIG.apiBase}/api/ai/conversations`, authHeaders());
    if (!res.ok) throw new Error(`Failed to fetch conversations (${res.status})`);
    return res.json() as Promise<AiConversation[]>;
  },

  /** Create a new AI conversation */
  async createAiConversation(title?: string): Promise<AiConversation> {
    const res = await fetch(`${CLIENT_CONFIG.apiBase}/api/ai/conversations`, authHeaders({
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ title }),
    }));
    if (!res.ok) throw new Error(`Failed to create conversation (${res.status})`);
    return res.json() as Promise<AiConversation>;
  },

  /** Delete an AI conversation */
  async deleteAiConversation(id: string): Promise<void> {
    const res = await fetch(
      `${CLIENT_CONFIG.apiBase}/api/ai/conversations/${encodeURIComponent(id)}`,
      authHeaders({ method: 'DELETE' }),
    );
    if (!res.ok) throw new Error(`Failed to delete conversation (${res.status})`);
  },

  /** Get messages for an AI conversation */
  async getAiMessages(conversationId: string): Promise<AiMessage[]> {
    const res = await fetch(
      `${CLIENT_CONFIG.apiBase}/api/ai/conversations/${encodeURIComponent(conversationId)}/messages`,
      authHeaders(),
    );
    if (!res.ok) throw new Error(`Failed to fetch messages (${res.status})`);
    return res.json() as Promise<AiMessage[]>;
  },

  /** Stream an AI response for a conversation via SSE */
  async *streamAiConversation(conversationId: string, content: string): AsyncGenerator<string> {
    const res = await fetch(
      `${CLIENT_CONFIG.apiBase}/api/ai/conversations/${encodeURIComponent(conversationId)}/chat`,
      authHeaders({
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content }),
      }),
    );

    if (!res.ok) {
      if (res.status === 404) {
        throw new Error('AI assistant is not configured — add OPENAI_API_KEY to the server');
      }
      const body = await res.json().catch(() => ({ error: 'Unknown error' })) as { error?: string };
      throw new Error(body.error ?? `AI request failed (${res.status})`);
    }

    if (!res.body) throw new Error('No response stream available');

    yield* readSseStream(res.body);
  },
};

/** Parse one SSE data line into a chunk, an end-of-stream marker, or nothing. */
function parseSseLine(line: string): { chunk?: string; done?: boolean } | null {
  if (!line.startsWith('data: ')) return null;
  const payload = line.slice(6);
  if (payload === '[DONE]') return { done: true };
  try {
    const parsed = JSON.parse(payload) as { chunk?: string; error?: string };
    if (parsed.error) {
      throw new Error(parsed.error);
    }
    return { chunk: parsed.chunk };
  } catch (e) {
    if (e instanceof SyntaxError) return null;
    throw e;
  }
}

/** Shared SSE stream reader */
async function* readSseStream(body: ReadableStream<Uint8Array>): AsyncGenerator<string> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() ?? '';

    for (const line of lines) {
      const event = parseSseLine(line);
      if (!event) continue;
      if (event.done) return;
      if (event.chunk) yield event.chunk;
    }
  }
}
